import type { FC } from "react"
import React, { useEffect, useRef } from "react"

import { useRecoilState, useRecoilValue } from "recoil"

import {
  findProjectedEdgeState,
  findProjectedNodeState,
} from "../../state/elements"
import { lastNodeIdState } from "../../state/elements/nodeSelection"
import {
  nodeSnapPointState,
  guideAngleSwapState,
} from "../../state/elements/transactions/previewEdgeLineLocking"

const useNodeSnapPoint = () => {
  const {
    previewNode,
    closestNodeSnap,
    whereLinesIntersect,
    possibleAngles: newPossibleAngles,
    previousNodeIdWeDrawFrom,
    rightAnglePoints,
  } = useRecoilValue(nodeSnapPointState)

  const [possibleAngles, setPossibleAngles] = useRecoilState(guideAngleSwapState)
  const highlightedEdgeId = possibleAngles.angles[possibleAngles.index]?.id
  const highlightedEdge = useRecoilValue(
    findProjectedEdgeState(highlightedEdgeId),
  )

  useEffect(() => {
    if (previousNodeIdWeDrawFrom !== possibleAngles.previousNodeIdWeDrawFrom) {
      setPossibleAngles({
        index: 0,
        previousNodeIdWeDrawFrom,
        angles: newPossibleAngles,
      })
    }
  }, [
    previousNodeIdWeDrawFrom,
    newPossibleAngles,
    possibleAngles,
    setPossibleAngles,
  ])

  useEffect(() => {
    return () => {
      setPossibleAngles({
        index: 0,
        previousNodeIdWeDrawFrom: null,
        angles: [],
      })
    }
  }, [setPossibleAngles])

  return {
    previewNode,
    closestNodeSnap,
    whereLinesIntersect,
    highlightedEdge,
    rightAnglePoints,
  }
}

export const PreviewEdge_Internal: FC = () => {
  const p1Id = useRecoilValue(lastNodeIdState)
  const p1 = useRecoilValue(findProjectedNodeState(p1Id || `no last node!`))

  const nodeRef = useRef(null)
  const {
    previewNode,
    closestNodeSnap,
    whereLinesIntersect,
    highlightedEdge,
    rightAnglePoints,
  } = useNodeSnapPoint()

  return p1Id ? (
    <g ref={nodeRef} style={{ pointerEvents: `none` }}>
      <line
        x1={p1.x}
        y1={p1.y}
        x2={previewNode.x}
        y2={previewNode.y}
        stroke={`#fff`}
        strokeWidth={3}
      />
      <line
        x1={p1.x}
        y1={p1.y}
        x2={previewNode.x}
        y2={previewNode.y}
        stroke="#000"
        strokeWidth={2}
        strokeDasharray={`4 9`}
      />

      {highlightedEdge && (
        <line
          x1={highlightedEdge.p1.x}
          y1={highlightedEdge.p1.y}
          x2={highlightedEdge.p2.x}
          y2={highlightedEdge.p2.y}
          {...highlightedEdge.props}
          stroke="#fd73a1"
          strokeWidth={2}
        />
      )}
      {whereLinesIntersect && (
        <circle
          cx={whereLinesIntersect.x}
          cy={whereLinesIntersect.y}
          r={5}
          fill={`blue`}
          stroke={`#000`}
          strokeWidth={2}
        />
      )}
      {closestNodeSnap && (
        <>
          <circle
            cx={closestNodeSnap.x}
            cy={closestNodeSnap.y}
            r={5}
            fill={`white`}
            stroke={`magenta`}
            strokeWidth={2}
          />
          <line
            x1={previewNode.x}
            y1={previewNode.y}
            x2={closestNodeSnap.x}
            y2={closestNodeSnap.y}
            stroke="magenta"
            strokeWidth={1}
          />
        </>
      )}
      {rightAnglePoints && (
        <>
          <circle
            cx={p1.x}
            cy={p1.y}
            r={5}
            fill="white"
            stroke="magenta"
            strokeWidth={2}
          />
          <line
            x1={rightAnglePoints.p1.x}
            y1={rightAnglePoints.p1.y}
            x2={rightAnglePoints.p3.x}
            y2={rightAnglePoints.p3.y}
            stroke="magenta"
            strokeWidth={1}
          />
          <line
            x1={rightAnglePoints.p2.x}
            y1={rightAnglePoints.p2.y}
            x2={rightAnglePoints.p3.x}
            y2={rightAnglePoints.p3.y}
            stroke="magenta"
            strokeWidth={1}
          />
        </>
      )}
      <circle cx={previewNode.x} cy={previewNode.y} r={5} fill="#fff" />
    </g>
  ) : null
}

export const PreviewEdge = React.memo(PreviewEdge_Internal)
